import { useState, useCallback } from 'react';
import axios from 'axios';
import { getToken } from '../../services/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOCK_QUESTIONS_TR = [
  { question: 'Kendinizi kısaca tanıtır mısınız?', category: 'Tanışma' },
  { question: 'Neden kabin memuru olmak istiyorsunuz?', category: 'Genel' },
  { question: 'Zor bir yolcuyla nasıl başa çıkarsınız?', category: 'Hizmet' },
  { question: 'Acil çıkış prosedürlerini anlatır mısınız?', category: 'Acil Durum' },
  { question: 'Takım çalışmasındaki rolünüzü nasıl tanımlarsınız?', category: 'Takım Çalışması' },
];

const MOCK_QUESTIONS_EN = [
  { question: 'Could you briefly introduce yourself?', category: 'Introduction' },
  { question: 'Why do you want to become a cabin crew member?', category: 'General' },
  { question: 'How do you handle a difficult passenger?', category: 'Service' },
  { question: 'Can you describe the emergency exit procedures?', category: 'Emergency' },
  { question: 'How would you describe your role in teamwork?', category: 'Teamwork' },
];

/**
 * useInterviewSimStart — POST /api/cabin/interview-sim/start
 * Returns { session_id, questions, difficulty, language }
 */
export function useInterviewSimStart() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const start = useCallback(async ({ difficulty = 3, language = 'tr' } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await axios.post(
        `${API}/cabin/interview-sim/start`,
        { difficulty, language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data; // { session_id, questions, difficulty, language }
    } catch (err) {
      const status = err?.response?.status;

      // 429 — cooldown active: surface real error, return sentinel so callers can distinguish
      if (status === 429) {
        const detail =
          typeof err?.response?.data?.detail === 'object'
            ? err.response.data.detail
            : err?.response?.data;
        const msg =
          detail?.message_tr ||
          detail?.message_en ||
          err?.response?.data?.message_tr ||
          err.message;
        setError(msg);
        // Return cooldown sentinel (truthy so callers must check __cooldown flag)
        return {
          __cooldown: true,
          error_code: detail?.error_code || 'interview_cooldown_active',
          next_available_at: detail?.next_available_at || null,
          cooldown_days: detail?.cooldown_days || null,
          message_tr: detail?.message_tr || null,
          message_en: detail?.message_en || null,
        };
      }

      // Mock fallback when backend is not ready (non-auth, non-cooldown errors)
      if (status !== 400 && status !== 401 && status !== 403) {
        const mockQuestions = language === 'en' ? MOCK_QUESTIONS_EN : MOCK_QUESTIONS_TR;
        const mockNow = new Date().toISOString();
        const mockExpires = new Date(Date.now() + 600_000).toISOString();
        return {
          session_id: `mock-${Date.now()}`,
          questions: mockQuestions,
          difficulty,
          language,
          total_duration_seconds: 600,
          started_at: mockNow,
          server_now: mockNow,
          expires_at: mockExpires,
        };
      }
      const msg = err?.response?.data?.message_tr || err?.response?.data?.detail || err.message;
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, start };
}
